import { BaseComponent } from '../../components/base-component'
import type { Nullable } from '../../types/common'
import type { AccordeonComponent } from '../../components/accordeon/accordeon'
import type { MapComponent } from './map-component'
import type { ILocationData } from './types'
import {
  $el,
  $list,
  isNotThemeStore,
  transitionToPromise,
} from 'src/scripts/utils/utils'
import { isMobile } from 'src/scripts/utils/check-media'

export class StoreLocator extends BaseComponent {
  mapSelector: string = '#map'
  storeElementSelector: string = 'data-store'
  storeIdSelector: string = 'data-block-id'
  storeNameSelector: string = 'data-name'
  storeCoordinatesSelector: string = 'data-coordinates'
  defaultImageSelector: string = 'map-default-image'

  map: Nullable<MapComponent>
  stores: AccordeonComponent[]

  constructor() {
    super()
    this.map = $el(this.mapSelector, this)
    this.stores = $list(`[${this.storeElementSelector}]`, this)
  }

  mountComponent(): void {
    if (this.map) {
      this.map.locationsData = this.generateLocationsData()
      this.map.initMap()

      this.map?.on('markerClick', this.handleMarkerClick)
      this.map?.on('mapError', this.handleMapError)
    }

    this.stores.forEach(store => {
      this.addListener(store, 'click', this.handleStoreClick)
    })
  }

  unmountComponent(): void {
    if (this.map) {
      this.map.off('markerClick', this.handleMarkerClick)
      this.map.off('mapError', this.handleMapError)
    }
  }

  generateLocationsData = (): ILocationData[] => {
    const locationsData = this.stores.map(store => ({
      coordinates: store.getAttribute(this.storeCoordinatesSelector),
      id: store.getAttribute(this.storeIdSelector),
    }))

    return locationsData
  }

  handleMarkerClick = ({ markerId }: { markerId: string }): void => {
    const storeToScrollTo = this.stores.find(
      store => store.dataset.blockId === markerId,
    )

    if (isNotThemeStore()) {
      storeToScrollTo?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    storeToScrollTo?.show()

    this.toggleStoreVisibility(markerId)
  }

  handleStoreClick = ({ currentTarget }): void => {
    const storeId = currentTarget.getAttribute(this.storeIdSelector)

    this.toggleStoreVisibility(storeId)

    if (isMobile() && currentTarget.isExpanded) {
      this.scrollToTabTop(currentTarget)
    }
  }

  scrollToTabTop = async (element: HTMLElement) => {
    await transitionToPromise(element)
    const top = element.getBoundingClientRect().top

    if (top < 0) {
      window.scrollBy({
        top: top - 100,
        left: 0,
        behavior: 'smooth',
      })
    }
  }

  toggleStoreVisibility = (id: string): void => {
    this.toggleStoreExpanded(id)
    this.toggleMarkerHighlighted(id)
  }

  toggleStoreExpanded = (id: string): void => {
    this.stores.forEach(store => {
      if (store.dataset.blockId !== id && store.isExpanded) {
        store.hide()
      }
    })
  }

  toggleMarkerHighlighted = (id: string): void => {
    const isAnyStoreExpanded = this.stores.find(store => store.isExpanded)

    if (isAnyStoreExpanded) {
      this.map?.highlightMarker(id)
    } else {
      this.map?.highlightMarker(null)
    }
  }

  setDefaultImage = (): void => {
    const template: HTMLTemplateElement = $el(
      `[${this.defaultImageSelector}]`,
      this,
    )
    const image = template?.content

    this.map?.replaceWith(image)
  }

  handleMapError = (): void => {
    this.setDefaultImage()
  }
}
